import React, {useState} from 'react';
import {Button, Confirm, Input, Modal, Spinner, Switch} from "@tag/tag-components-react-v4";
import {CustomerDTO} from "../types/CustomerDTO";
import CustomerService from "../services/CustomerService";

import styles from './UnlinkWorkspaceModal.module.scss';
import {ProjectAccents} from "../../General/config/projectAccents";

interface EmployeeMatch {
    employeeId: string;
    firstName?: string;
    lastName?: string;
    displayName: string;
}

function UnlinkEmployeeModal(props: Readonly<{
    visible: boolean,
    selectedCustomer: CustomerDTO,
    handleClose: Function,
    handleUnlinkWorkspaceModalClose: Function,
    handleCustomerMenuModalClose: Function,
    setAlert: Function
}>) {

    const [searchByName, setSearchByName] = useState(false);
    const [employeeId, setEmployeeId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [loading, setLoading] = useState<string|undefined>(undefined);
    const [matches, setMatches] = useState<EmployeeMatch[]>([]);
    const [employeeToUnlink, setEmployeeToUnlink] = useState<EmployeeMatch|undefined>(undefined);

    const isFindDisabled = searchByName
        ? !firstName.trim() && !lastName.trim()
        : !employeeId.trim();

    const handleError = () => {
        props.setAlert({type: 'error', message: 'An error has occurred, please contact HSC Care DevOps'});
        props.handleClose();
        props.handleUnlinkWorkspaceModalClose();
        props.handleCustomerMenuModalClose();
    };

    const handleFind = async () => {
        setLoading('Searching...');
        setMatches([]);

        try {
            const result = searchByName
                ? await CustomerService.findEmployeesByName(props.selectedCustomer.clientId, firstName.trim(), lastName.trim(), props.selectedCustomer.environmentName)
                : await CustomerService.findEmployeeById(props.selectedCustomer.clientId, employeeId.trim(), props.selectedCustomer.environmentName);

            if (!result) {
                props.setAlert({type: 'warning', message: 'No employee found matching the details provided'});
                return;
            }

            if (searchByName) {
                setMatches(result as EmployeeMatch[]);
            } else {
                setEmployeeToUnlink(result as EmployeeMatch);
            }
        } catch {
            handleError();
        } finally {
            setLoading(undefined);
        }
    };

    const handleUnlink = async () => {
        const employee = employeeToUnlink;
        setEmployeeToUnlink(undefined);

        if (!employee) {
            return;
        }

        setLoading('Unlinking employee...');

        try {
            await CustomerService.unlinkEmployee(props.selectedCustomer.clientId, employee.employeeId, props.selectedCustomer.environmentName);
            props.setAlert({type: 'success', message: `Employee '${employee.displayName}' unlinked from Access Workspace`});
            props.handleClose();
            props.handleUnlinkWorkspaceModalClose();
            props.handleCustomerMenuModalClose();
        } catch {
            handleError();
        } finally {
            setLoading(undefined);
        }
    };

    const renderModalContent = () => {
        if (loading) {
            return (
                <div className={styles.UnlinkWorkspaceModalLoaderContainer}>
                    <Spinner>{loading}</Spinner>
                </div>
            );
        }

        return (
            <div style={{marginTop: '16px'}}>
                <div className={styles.UnlinkWorkspaceModalField}>
                    <Switch
                        checked={searchByName}
                        heading={searchByName ? 'Search by Name' : 'Search by ID'}
                        accent={ProjectAccents.PeoplePlanner}
                        onChange={(e) => {
                            setSearchByName(!!e.checked);
                            setMatches([]);
                        }}
                    />
                </div>
                {searchByName ?
                    <>
                        <div className={styles.UnlinkWorkspaceModalField}>
                            <Input
                                placeholder="First name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.currentTarget.value)}
                                style={{width: '100%'}}
                            />
                        </div>
                        <div className={styles.UnlinkWorkspaceModalField}>
                            <Input
                                placeholder="Last name"
                                value={lastName}
                                onChange={(e) => setLastName(e.currentTarget.value)}
                                style={{width: '100%'}}
                            />
                        </div>
                    </> :
                    <div className={styles.UnlinkWorkspaceModalField}>
                        <Input
                            placeholder="Employee ID"
                            value={employeeId}
                            onChange={(e) => setEmployeeId(e.currentTarget.value)}
                            style={{width: '100%'}}
                        />
                    </div>
                }
                <div className={styles.UnlinkWorkspaceModalButtonElement}>
                    <Button text={'Find'} disabled={isFindDisabled} onClick={handleFind} />
                </div>
                {matches.length > 0 ?
                    <div style={{marginTop: '1em'}}>
                        {matches.map((employee) => (
                            <div key={employee.employeeId} className={styles.UnlinkWorkspaceModalResultRow}>
                                <span>{employee.displayName} ({employee.employeeId})</span>
                                <Button text={'Unlink'} onClick={() => setEmployeeToUnlink(employee)} />
                            </div>
                        ))}
                    </div> : undefined
                }
            </div>
        );
    };

    return (
        <>
            <Modal
                visible={props.visible && !employeeToUnlink}
                accent={ProjectAccents.PeoplePlanner}
                heading={'Unlink Employee'}
                onCancelButtonClick={() => props.handleClose()}
                onClose={() => props.handleClose()}
                primaryButtonProps={{buttonStyle: {'display': 'none'}}}
            >
                <div>
                    {renderModalContent()}
                </div>
            </Modal>
            {employeeToUnlink ?
                <Confirm
                    visible={!!employeeToUnlink}
                    type="warning"
                    accent={ProjectAccents.PeoplePlanner}
                    heading={'Are you sure?'}
                    onPrimaryButtonClick={handleUnlink}
                    onCancelButtonClick={() => setEmployeeToUnlink(undefined)}
                    onClose={() => setEmployeeToUnlink(undefined)}
                >
                    <div style={{lineHeight: '1.6'}}>
                        <p>This will unlink <strong>{employeeToUnlink.displayName}</strong> (ID {employeeToUnlink.employeeId}) from their Access Workspace account.</p>
                        <p>Their workspace link and workspace log entries will be removed.</p>
                    </div>
                </Confirm> : undefined
            }
        </>
    );
}

export default UnlinkEmployeeModal;
